import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, User, Calendar, MapPin, CreditCard, FileText,
    Download, FileSpreadsheet, FileJson, Loader2, Star,
    Phone, Mail, CheckCircle, XCircle, Clock, Filter,
    Car, DollarSign, Shield, FileCheck, Send, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import DataTablePagination from '@/components/ui/data-table-pagination';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { useDriverProfile, useDriverTripHistory, useExportDriverData, useSendMonthlySummary } from '@/api-services/hooks/useUserProfile';
import { TripStatus, ExportFormat, ExportDataOptions } from '@/api-services/hooks/useUserProfile';
import { useDocumentTempUrls } from '@/api-services/hooks/useDriver';
import { getDocumentViewUrl } from '@/api-services/config';
import KycStatusBadge from '@/components/drivers/KycStatusBadge';
import { formatGlobalCurrency } from '@/api-services/hooks/useSettings';

const DriverProfilePage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const driverId = parseInt(id || '0');

    // Local state
    const [activeTab, setActiveTab] = useState('profile');
    const [tripStatusFilter, setTripStatusFilter] = useState<TripStatus | 'all'>('all');
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [exportFormat, setExportFormat] = useState<ExportFormat>('CSV');
    const [includeProfile, setIncludeProfile] = useState(true);
    const [includeTrips, setIncludeTrips] = useState(true);
    const [includeStats, setIncludeStats] = useState(true);

    // Monthly summary dialog state
    const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
    const [summaryMonth, setSummaryMonth] = useState(new Date().getMonth() + 1);
    const [summaryYear, setSummaryYear] = useState(new Date().getFullYear());
    const [summaryEmail, setSummaryEmail] = useState('');

    // API Hooks
    const { data: driver, loading: isLoadingDriver, refetch: refetchDriver } = useDriverProfile(driverId, {
        enabled: !!driverId,
    });

    const { data: tripHistory, isLoading: isLoadingTrips } = useDriverTripHistory(
        driverId,
        {
            page: currentPage,
            size: itemsPerPage,
            status: tripStatusFilter === 'all' ? undefined : tripStatusFilter,
        },
        { enabled: !!driverId && (activeTab === 'trips' || activeTab === 'documents') }
    );

    const { mutate: exportDriverData, isPending: isExporting } = useExportDriverData({
        onSuccess: () => {
            toast.success('Driver data exported successfully');
        },
        onError: (error) => {
            toast.error(`Export failed: ${error.message}`);
        },
    });

    const { mutate: sendMonthlySummary, isPending: isSendingSummary } = useSendMonthlySummary({
        onSuccess: (data) => {
            toast.success(data.message || 'Monthly summary sent successfully');
            setSummaryDialogOpen(false);
        },
        onError: (error) => {
            toast.error(`Failed to send summary: ${error.message}`);
        },
    });

    // Reset pagination when filter changes
    useEffect(() => {
        setCurrentPage(0);
    }, [tripStatusFilter]);

    // Handle export
    const handleExport = () => {
        const options: ExportDataOptions = {
            format: exportFormat,
            includeProfile,
            includeTrips,
            includeStats,
        };
        exportDriverData({ driverId, options });
    };

    // Handle monthly summary
    const handleSendMonthlySummary = () => {
        sendMonthlySummary({
            driverId,
            month: summaryMonth,
            year: summaryYear,
            email: summaryEmail || undefined,
        });
    };

    // Get status badge for trips
    const getTripStatusBadge = (status: TripStatus) => {
        const config: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
            PENDING: { variant: 'outline', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
            ACCEPTED: { variant: 'outline', className: 'bg-blue-100 text-blue-800 border-blue-300' },
            IN_PROGRESS: { variant: 'default', className: 'bg-green-500 text-white' },
            COMPLETED: { variant: 'default', className: 'bg-emerald-600 text-white' },
            CANCELLED: { variant: 'destructive', className: '' },
            FAILED: { variant: 'secondary', className: 'bg-gray-500 text-white' },
        };
        const badgeConfig = config[status] || { variant: 'outline', className: '' };
        return <Badge variant={badgeConfig.variant} className={badgeConfig.className}>{status.replace('_', ' ')}</Badge>;
    };

    // Format currency
    const formatCurrency = (amount?: number) => {
        if (amount === undefined || amount === null) return 'N/A';
        return `$${Number(amount).toFixed(2)}`;
    };

    // Format date
    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    // Get initials for avatar
    const getInitials = (name?: string) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    // Collect all raw file paths from the driver record
    const allProfileRawPaths = [
        driver?.govtIdFrontUrl,
        driver?.govtIdBackUrl,
        driver?.drivingLicenseFrontUrl,
        driver?.drivingLicenseBackUrl,
        driver?.selfieWithIdUrl,
        driver?.vehicleRegistrationCertUrl || driver?.ownershipDocUrl,
        driver?.vehicleInsuranceCertUrl || driver?.insuranceDocUrl,
        driver?.roadTaxDocUrl,
        driver?.vehicleInspectionCertUrl,
        driver?.profilePhotoUrl,
    ].filter((p): p is string => !!p);

    // Resolve all document file paths to temporary HTTP URLs via the API
    const { urlMap: profileTempUrlMap } = useDocumentTempUrls(allProfileRawPaths);

    // Helper: given a raw file path, return the resolved temp URL (or null)
    const getDocumentUrl = (url?: string | null) => {
        if (!url) return null;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        // Try temp URL first, otherwise use document download endpoint
        if (profileTempUrlMap[url]) return profileTempUrlMap[url];
        return getDocumentViewUrl(url);
    };

    // Prepare documents for viewing
    const kycDocuments = [
        { label: 'Govt ID Front', url: getDocumentUrl(driver?.govtIdFrontUrl), fileName: 'govt-id-front' },
        { label: 'Govt ID Back', url: getDocumentUrl(driver?.govtIdBackUrl), fileName: 'govt-id-back' },
        { label: 'Driving License Front', url: getDocumentUrl(driver?.drivingLicenseFrontUrl), fileName: 'license-front' },
        { label: 'Driving License Back', url: getDocumentUrl(driver?.drivingLicenseBackUrl), fileName: 'license-back' },
        { label: 'Selfie with ID', url: getDocumentUrl(driver?.selfieWithIdUrl), fileName: 'selfie' },
    ].filter(d => d.url);

    const vehicleDocuments = [
        { label: 'Vehicle Registration', url: getDocumentUrl(driver?.vehicleRegistrationCertUrl || driver?.ownershipDocUrl), fileName: 'registration' },
        { label: 'Insurance Document', url: getDocumentUrl(driver?.vehicleInsuranceCertUrl || driver?.insuranceDocUrl), fileName: 'insurance' },
        { label: 'Road Tax Document', url: getDocumentUrl(driver?.roadTaxDocUrl), fileName: 'road-tax' },
        { label: 'Vehicle Inspection', url: getDocumentUrl(driver?.vehicleInspectionCertUrl), fileName: 'inspection' },
    ].filter(d => d.url);

    // Loading state
    if (isLoadingDriver) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    // Not found state
    if (!driver) {
        return (
            <div className="p-8 text-center">
                <h2 className="text-2xl font-bold text-destructive">Driver not found</h2>
                <Button onClick={() => navigate('/admin/drivers')} className="mt-4">
                    Back to Drivers
                </Button>
            </div>
        );
    }

    const trips = tripHistory?.content || [];
    const totalTrips = tripHistory?.page?.totalElements || 0;
    const totalPages = tripHistory?.page?.totalPages || 1;

    // Calculate total earnings from trips if available
    const totalTripEarnings = trips.reduce((sum, trip) => sum + (trip.driverEarnings || 0), 0);

    return (
        <div className="space-y-6 animate-fade-in pb-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/admin/drivers')}
                        className="hover:bg-muted"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                    <div className="flex items-center gap-3">
                        <Avatar className="h-14 w-14 border-2 border-primary/10">
                            <AvatarImage src={getDocumentUrl(driver.profilePhotoUrl) || ''} alt={driver.fullName} />
                            <AvatarFallback className="bg-primary/10 text-primary text-lg">
                                {getInitials(driver.fullName)}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <h1 className="text-2xl font-bold text-foreground">{driver.fullName || 'Unknown'}</h1>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Phone className="w-3 h-3" />
                                <span>{driver.phoneNumber || 'N/A'}</span>
                                <span>•</span>
                                <Mail className="w-3 h-3" />
                                <span>{driver.email || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 bg-green-50 px-3 py-1.5 rounded-full border border-green-200">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span className="font-medium">{formatGlobalCurrency(driver.totalEarnings || totalTripEarnings)}</span>
                    </div>
                    <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1.5 rounded-full border border-yellow-200">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="font-medium">{driver.driverRating || driver.averageRating || 'N/A'}</span>
                    </div>
                    <KycStatusBadge status={driver.kycStatus || 'PENDING'} />
                    <Badge variant={driver.isActive ? 'default' : 'secondary'}>
                        {driver.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="grid w-full grid-cols-4 h-auto">
                    <TabsTrigger value="profile" className="py-2">
                        <User className="w-4 h-4 mr-2" />
                        Profile Info
                    </TabsTrigger>
                    <TabsTrigger value="trips" className="py-2">
                        <MapPin className="w-4 h-4 mr-2" />
                        Trip History
                    </TabsTrigger>
                    <TabsTrigger value="documents" className="py-2">
                        <FileCheck className="w-4 h-4 mr-2" />
                        Documents
                    </TabsTrigger>
                    <TabsTrigger value="export" className="py-2">
                        <Download className="w-4 h-4 mr-2" />
                        Export Data
                    </TabsTrigger>
                </TabsList>

                {/* Profile Tab */}
                <TabsContent value="profile" className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Personal Details */}
                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <User className="w-5 h-5 text-primary" />
                                    Personal Details
                                </CardTitle>
                                <CardDescription>Driver's personal information</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Full Name</Label>
                                        <p className="font-medium">{driver.fullName || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Phone Number</Label>
                                        <p className="font-medium">{driver.phoneNumber || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Email</Label>
                                        <p className="font-medium">{driver.email || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Date of Birth</Label>
                                        <p className="font-medium">{formatDate(driver.dateOfBirth)}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Gender</Label>
                                        <p className="font-medium">{driver.gender || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Preferred Language</Label>
                                        <p className="font-medium">{driver.preferredLanguage || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Emergency Contact</Label>
                                        <p className="font-medium">{driver.emergencyContactNumber || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Signup Method</Label>
                                        <p className="font-medium">{driver.signupMethod || 'N/A'}</p>
                                    </div>
                                </div>
                                <Separator />
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">Address</Label>
                                    <p className="font-medium">{driver.address || 'N/A'}</p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Stats Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-primary" />
                                    Statistics
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Total Trips</span>
                                    <span className="font-bold text-lg">{driver.totalRidesCompleted || driver.totalTrips || 0}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Total Earnings</span>
                                    <span className="font-bold text-lg text-green-600">{formatGlobalCurrency(driver.totalEarnings)}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Rating</span>
                                    <div className="flex items-center gap-1">
                                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                        <span className="font-bold text-lg">{driver.driverRating || driver.averageRating || 'N/A'}</span>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Acceptance Rate</span>
                                    <span className="font-bold text-lg">{driver.acceptanceRate ? `${driver.acceptanceRate}%` : 'N/A'}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Online Hours (Month)</span>
                                    <span className="font-bold text-lg">{driver.onlineHoursThisMonth ? `${driver.onlineHoursThisMonth}h` : 'N/A'}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Member Since</span>
                                    <span className="font-medium">{formatDate(driver.memberSince || driver.createdDate)}</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Vehicle Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Car className="w-5 h-5 text-primary" />
                                Vehicle Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Vehicle Type</Label>
                                        <p className="font-medium">{driver.vehicleType || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Make</Label>
                                        <p className="font-medium">{driver.vehicleMake || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Model</Label>
                                        <p className="font-medium">{driver.vehicleModel || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Registration Number</Label>
                                        <p className="font-medium">{driver.vehicleRegistrationNumber || driver.vehicleLicensePlate || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Color</Label>
                                        <p className="font-medium">{driver.vehicleColor || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Year</Label>
                                        <p className="font-medium">{driver.yearOfManufacture || driver.vehicleYear || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Seating Capacity</Label>
                                        <p className="font-medium">{driver.seatingCapacity || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Luggage Capacity</Label>
                                        <p className="font-medium">{driver.luggageCapacity ? `${driver.luggageCapacity} kg` : 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Banking Details */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-primary" />
                                Banking & Payout Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">Account Holder</Label>
                                    <p className="font-medium">{driver.bankAccountHolderName || 'N/A'}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">Bank Name</Label>
                                    <p className="font-medium">{driver.bankName || 'N/A'}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">Account Number</Label>
                                    <p className="font-medium">{driver.bankAccountNumber || driver.accountNumber ? `****${(driver.bankAccountNumber || driver.accountNumber).slice(-4)}` : 'N/A'}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">IFSC Code</Label>
                                    <p className="font-medium">{driver.bankIfscCode || driver.ifscCode || 'N/A'}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">SWIFT Code</Label>
                                    <p className="font-medium">{driver.swiftCode || 'N/A'}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">Payment Frequency</Label>
                                    <p className="font-medium">{driver.paymentFrequency || 'N/A'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Trip History Tab */}
                <TabsContent value="trips">
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <MapPin className="w-5 h-5 text-primary" />
                                        Trip History
                                    </CardTitle>
                                    <CardDescription>All completed trips with earnings</CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Filter className="w-4 h-4 text-muted-foreground" />
                                    <Select value={tripStatusFilter} onValueChange={(value) => setTripStatusFilter(value as TripStatus | 'all')}>
                                        <SelectTrigger className="w-[160px]">
                                            <SelectValue placeholder="Filter by status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Statuses</SelectItem>
                                            <SelectItem value="PENDING">Pending</SelectItem>
                                            <SelectItem value="ACCEPTED">Accepted</SelectItem>
                                            <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                                            <SelectItem value="COMPLETED">Completed</SelectItem>
                                            <SelectItem value="CANCELLED">Cancelled</SelectItem>
                                            <SelectItem value="FAILED">Failed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Trip ID</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Pickup</TableHead>
                                            <TableHead>Dropoff</TableHead>
                                            <TableHead>Rider</TableHead>
                                            <TableHead>Fare</TableHead>
                                            <TableHead>Earnings</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {isLoadingTrips ? (
                                            <TableRow>
                                                <TableCell colSpan={8} className="text-center py-8">
                                                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                                                </TableCell>
                                            </TableRow>
                                        ) : trips.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                                    No trips found
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            trips.map((trip) => (
                                                <TableRow key={trip.tripId} className="hover:bg-muted/50">
                                                    <TableCell className="font-medium">#{trip.tripId}</TableCell>
                                                    <TableCell>{formatDate(trip.requestTime)}</TableCell>
                                                    <TableCell className="max-w-[120px] truncate" title={trip.pickupLocation}>
                                                        {trip.pickupLocation}
                                                    </TableCell>
                                                    <TableCell className="max-w-[120px] truncate" title={trip.dropoffLocation}>
                                                        {trip.dropoffLocation}
                                                    </TableCell>
                                                    <TableCell>{trip.riderName || 'N/A'}</TableCell>
                                                    <TableCell>{formatGlobalCurrency(trip.totalFare || trip.fare)}</TableCell>
                                                    <TableCell className="font-medium text-green-600">
                                                        {formatGlobalCurrency(trip.driverEarnings)}
                                                    </TableCell>
                                                    <TableCell>{getTripStatusBadge(trip.status)}</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                                {!isLoadingTrips && trips.length > 0 && (
                                    <DataTablePagination
                                        currentPage={currentPage + 1}
                                        totalPages={totalPages}
                                        totalItems={totalTrips}
                                        itemsPerPage={itemsPerPage}
                                        onPageChange={(page) => setCurrentPage(page - 1)}
                                        onItemsPerPageChange={(size) => {
                                            setItemsPerPage(size);
                                            setCurrentPage(0);
                                        }}
                                        className="mt-4"
                                    />
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="documents" className="space-y-6">
                    {/* KYC Documents */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="w-5 h-5 text-primary" />
                                KYC Documents
                            </CardTitle>
                            <CardDescription>
                                ID Type: <span className="font-medium">{driver.govtIdType || 'N/A'}</span> | 
                                ID Number: <span className="font-medium">{driver.govtIdNumber || 'N/A'}</span>
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {kycDocuments.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {kycDocuments.map((doc) => (
                                        <div key={doc.fileName} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                                            <div className="flex items-center gap-2 mb-2">
                                                <FileText className="w-5 h-5 text-primary" />
                                                <span className="font-medium">{doc.label}</span>
                                            </div>
                                            <a
                                                href={doc.url!}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-primary hover:underline"
                                            >
                                                View Document
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted-foreground text-center py-8">No KYC documents available</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Vehicle Documents */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Car className="w-5 h-5 text-primary" />
                                Vehicle Documents
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {vehicleDocuments.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {vehicleDocuments.map((doc) => (
                                        <div key={doc.fileName} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                                            <div className="flex items-center gap-2 mb-2">
                                                <FileCheck className="w-5 h-5 text-primary" />
                                                <span className="font-medium">{doc.label}</span>
                                            </div>
                                            <a
                                                href={doc.url!}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-primary hover:underline"
                                            >
                                                View Document
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted-foreground text-center py-8">No vehicle documents available</p>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Export Tab */}
                <TabsContent value="export">
                    <div className="max-w-2xl mx-auto space-y-6">
                        {/* Export Data Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Download className="w-5 h-5 text-primary" />
                                    Export Driver Data
                                </CardTitle>
                                <CardDescription>
                                    Download driver information in various formats
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Export Format */}
                                <div className="space-y-3">
                                    <Label>Export Format</Label>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {(['CSV', 'PDF', 'JSON', 'XLSX'] as ExportFormat[]).map((format) => (
                                            <button
                                                key={format}
                                                onClick={() => setExportFormat(format)}
                                                className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                                                    exportFormat === format
                                                        ? 'border-primary bg-primary/5'
                                                        : 'border-muted hover:border-primary/50'
                                                }`}
                                            >
                                                {format === 'CSV' && <FileSpreadsheet className="w-6 h-6 mb-2 text-green-600" />}
                                                {format === 'PDF' && <FileText className="w-6 h-6 mb-2 text-red-600" />}
                                                {format === 'JSON' && <FileJson className="w-6 h-6 mb-2 text-blue-600" />}
                                                {format === 'XLSX' && <FileSpreadsheet className="w-6 h-6 mb-2 text-green-700" />}
                                                <span className="font-medium">{format}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* Include Options */}
                                <div className="space-y-3">
                                    <Label>Include in Export</Label>
                                    <div className="space-y-3">
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="includeProfile"
                                                checked={includeProfile}
                                                onCheckedChange={(checked) => setIncludeProfile(checked as boolean)}
                                            />
                                            <Label htmlFor="includeProfile" className="cursor-pointer">
                                                Profile Information
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="includeTrips"
                                                checked={includeTrips}
                                                onCheckedChange={(checked) => setIncludeTrips(checked as boolean)}
                                            />
                                            <Label htmlFor="includeTrips" className="cursor-pointer">
                                                Trip History
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="includeStats"
                                                checked={includeStats}
                                                onCheckedChange={(checked) => setIncludeStats(checked as boolean)}
                                            />
                                            <Label htmlFor="includeStats" className="cursor-pointer">
                                                Statistics & Analytics
                                            </Label>
                                        </div>
                                    </div>
                                </div>

                                <Separator />

                                {/* Preview */}
                                <div className="bg-muted p-4 rounded-lg">
                                    <h4 className="font-medium mb-2">Export Preview</h4>
                                    <div className="text-sm text-muted-foreground space-y-1">
                                        <p>Driver: <span className="font-medium text-foreground">{driver.fullName}</span></p>
                                        <p>Format: <span className="font-medium text-foreground">{exportFormat}</span></p>
                                        <p>Includes:</p>
                                        <ul className="list-disc list-inside ml-2">
                                            {includeProfile && <li>Profile information</li>}
                                            {includeTrips && <li>Trip history ({driver.totalRidesCompleted || driver.totalTrips || 0} trips)</li>}
                                            {includeStats && <li>Statistics and analytics</li>}
                                        </ul>
                                    </div>
                                </div>

                                {/* Export Button */}
                                <Button
                                    onClick={handleExport}
                                    disabled={isExporting || (!includeProfile && !includeTrips && !includeStats)}
                                    className="w-full"
                                >
                                    {isExporting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4 mr-2" />
                                            Export Data
                                        </>
                                    )}
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Monthly Summary Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Send className="w-5 h-5 text-primary" />
                                    Send Monthly Summary
                                </CardTitle>
                                <CardDescription>
                                    Email monthly earnings summary to the driver
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="bg-muted p-4 rounded-lg">
                                    <h4 className="font-medium mb-2">What will be included?</h4>
                                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                                        <li>Total trips completed</li>
                                        <li>Total earnings</li>
                                        <li>Online hours</li>
                                        <li>Performance rating</li>
                                        <li>Incentives and deductions</li>
                                        <li>Net payout amount</li>
                                    </ul>
                                </div>
                                <Button
                                    onClick={() => setSummaryDialogOpen(true)}
                                    variant="outline"
                                    className="w-full"
                                >
                                    <Send className="w-4 h-4 mr-2" />
                                    Send Monthly Summary
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Monthly Summary Dialog */}
            <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Send className="w-5 h-5 text-primary" />
                            Send Monthly Summary
                        </DialogTitle>
                        <DialogDescription>
                            Send {driver.fullName}'s monthly earnings summary via email
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Month</Label>
                                <Select value={String(summaryMonth)} onValueChange={(value) => setSummaryMonth(parseInt(value))}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({ length: 12 }, (_, i) => (
                                            <SelectItem key={i + 1} value={String(i + 1)}>
                                                {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Year</Label>
                                <Select value={String(summaryYear)} onValueChange={(value) => setSummaryYear(parseInt(value))}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({ length: 5 }, (_, i) => {
                                            const year = new Date().getFullYear() - i;
                                            return (
                                                <SelectItem key={year} value={String(year)}>
                                                    {year}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Email (optional)</Label>
                            <Input
                                type="email"
                                placeholder={driver.email || 'driver@example.com'}
                                value={summaryEmail}
                                onChange={(e) => setSummaryEmail(e.target.value)}
                            />
                            <p className="text-xs text-muted-foreground">
                                Leave empty to use driver's registered email ({driver.email || 'not available'})
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSummaryDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSendMonthlySummary} disabled={isSendingSummary}>
                            {isSendingSummary ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Send Summary
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DriverProfilePage;
